import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import trpc from "@/shared/utils/trpc";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import Cookies from "js-cookie";
import { Prisma } from "@gt/prisma";
import { useNavigate, useLocation } from "react-router-dom";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { readGuestShortlistCompanyIds, removeGuestShortlistCompanyIds } from "@/shared/utils/guestShortlistStorage";

type UserWithRelations = Prisma.UserGetPayload<{
  include: {
    company: true;
  };
}>;

interface UserContextType {
  isLoggedIn: boolean;
  hasProfile: boolean | null;
  setHasProfile: React.Dispatch<React.SetStateAction<boolean | null>>;
  userDetails: UserWithRelations | null | undefined;
  setUserDetails: React.Dispatch<React.SetStateAction<UserWithRelations | null | undefined>>;
  /** Resolves to the loaded user (or null). Use for post-login navigation from `company.status`. */
  refetchUser: () => Promise<UserWithRelations | null>;

  shortlistedIds: Set<string>;
  addToShortlist: (companyId: string) => Promise<void>;
  removeFromShortlist: (companyId: string) => Promise<void>;

  interestedCompanyIds: Set<string>;
  hasCollaborationWith: (companyId: string) => boolean;
  addInterestedCompany: (companyId: string) => void;
  removeInterestedCompany: (companyId: string) => void;

  /** Pending collaboration requests: received = others requested you; sent = you requested others */
  pendingRequestCounts: { received: number; sent: number };
  refreshPendingInterestCount: () => void;

  collaborationSidebarCounts: { active: number; closed: number };
  refreshCollaborationSidebarCounts: () => void;
  syncGuestShortlistToServer: () => Promise<void>;

  maintenanceEnabled: boolean | null;
  refetchMaintenance: () => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used inside UserContextProvider");
  }
  return context;
};

export const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [userDetails, setUserDetails] = useState<UserWithRelations | null | undefined>(undefined);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
  const [shortlistedIds, setShortlistedIds] = useState<Set<string>>(new Set());
  const [interestedCompanyIds, setInterestedCompanyIds] = useState<Set<string>>(new Set());
  const [pendingRequestCounts, setPendingRequestCounts] = useState({ received: 0, sent: 0 });
  const [collaborationSidebarCounts, setCollaborationSidebarCounts] = useState({ active: 0, closed: 0 });

  const isLoggedIn = !!userDetails;

  /* ---------------- USER ---------------- */
  const fetchShortlistedIds = async () => {
    try {
      const ids = (await trpc.user.getShortlistedIds.query()) as string[];
      setShortlistedIds(new Set(ids));
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted IDs:", err);
      return [];
    }
  };

  const syncGuestShortlistToServer = useCallback(async () => {
    const pendingIds = readGuestShortlistCompanyIds();
    if (pendingIds.length === 0) return;

    const currentShortlisted = await fetchShortlistedIds();
    const alreadyShortlisted = pendingIds.filter((id) => currentShortlisted.includes(id));
    if (alreadyShortlisted.length > 0) {
      removeGuestShortlistCompanyIds(alreadyShortlisted);
    }

    const remaining = pendingIds.filter((id) => !alreadyShortlisted.includes(id));
    if (remaining.length === 0) return;

    const synced: string[] = [];
    for (const companyId of remaining) {
      try {
        await trpc.user.shortlistCompany.mutate({ companyId });
        synced.push(companyId);
      } catch (err) {
        console.error("Failed to sync guest shortlist company:", companyId, err);
      }
    }

    if (synced.length > 0) {
      removeGuestShortlistCompanyIds(synced);
      setShortlistedIds((prev) => new Set([...prev, ...synced]));
    }
  }, []);

  const fetchUserDetails = useCallback((): Promise<UserWithRelations | null> => {
    const token = Cookies.get(COOKIE_KEYS.token);

    if (!token) {
      setUserDetails(null);
      setHasProfile(false);
      setShortlistedIds(new Set());
      setInterestedCompanyIds(new Set());
      return Promise.resolve(null);
    }

    // Was "no session" (null) → loading (undefined) so ProtectedRoute does not redirect to /login
    // before getProfile finishes. Keeps existing user object during background refetch.
    setUserDetails((prev) => (prev === null ? undefined : prev));

    return trpc.user.getProfile
      .query({})
      .then(async (res) => {
        if (!res) return null;
        const user = res as UserWithRelations;
        setUserDetails(user);
        setHasProfile(true);
        await fetchShortlistedIds();
        void syncGuestShortlistToServer();
        fetchInterestedCompanyIds();
        fetchPendingRequestCounts();
        fetchCollaborationSidebarCounts();
        return user;
      })
      .catch((err) => {
        handleTrpcError(err);
        setHasProfile(false);
        setUserDetails(null);
        setShortlistedIds(new Set());
        setInterestedCompanyIds(new Set());
        return null;
      });
  }, [syncGuestShortlistToServer]);

  const fetchPendingRequestCounts = useCallback(() => {
    trpc.collaboration.getPendingRequestCounts
      .query()
      .then((res) => {
        setPendingRequestCounts({ received: res.received, sent: res.sent });
      })
      .catch((err) => {
        console.error("Error fetching pending request counts:", err);
      });
  }, []);

  const fetchCollaborationSidebarCounts = useCallback(() => {
    trpc.collaboration.getCollaborationSidebarCounts
      .query()
      .then((res) => {
        setCollaborationSidebarCounts({ active: res.active, closed: res.closed });
      })
      .catch((err) => {
        console.error("Error fetching collaboration sidebar counts:", err);
      });
  }, []);

  const fetchInterestedCompanyIds = () => {
    trpc.collaboration.getInterestedCompanyIds
      .query()
      .then((companyIds: string[]) => {
        setInterestedCompanyIds(new Set(companyIds));
      })
      .catch((err) => {
        console.error("Error fetching interested company IDs:", err);
      });
  };

  const hasCollaborationWith = (companyId: string) => {
    return interestedCompanyIds.has(companyId);
  };

  const addInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => new Set([...prev, companyId]));
  };

  const removeInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const addToShortlist = async (companyId: string) => {
    await trpc.user.shortlistCompany.mutate({ companyId });
    setShortlistedIds((prev) => new Set([...prev, companyId]));
  };

  const removeFromShortlist = async (companyId: string) => {
    await trpc.user.removeShortlist.mutate({ companyId });
    setShortlistedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const logout = () => {
    Cookies.remove(COOKIE_KEYS.token);
    setUserDetails(null);
    setHasProfile(null);
    setShortlistedIds(new Set());
    setInterestedCompanyIds(new Set());
    navigate("/login");
  };

  /* ---------------- MAINTENANCE ---------------- */
  const fetchMaintenance = () => {
    trpc.maintenance.get
      .query()
      .then((res) => {
        setMaintenanceEnabled(!!res?.webMaintenanceEnabled);
      })
      .catch((err) => {
        console.error("Error fetching maintenance:", err);
        setMaintenanceEnabled(false);
      });
  };

  /* ---------------- REDIRECT LOGIC ---------------- */
  useEffect(() => {
    if (maintenanceEnabled === null) return;

    // If maintenance is ON → force maintenance page
    if (maintenanceEnabled && location.pathname !== "/maintenance") {
      navigate("/maintenance", { replace: true });
    }

    // If maintenance is OFF → prevent staying on maintenance page
    if (!maintenanceEnabled && location.pathname === "/maintenance") {
      navigate("/login", { replace: true });
    }
  }, [maintenanceEnabled, location.pathname, navigate]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    fetchMaintenance();
    fetchUserDetails();
  }, []);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        hasProfile,
        setHasProfile,
        userDetails,
        setUserDetails,
        refetchUser: fetchUserDetails,
        shortlistedIds,
        addToShortlist,
        removeFromShortlist,
        interestedCompanyIds,
        hasCollaborationWith,
        addInterestedCompany,
        removeInterestedCompany,
        pendingRequestCounts,
        refreshPendingInterestCount: fetchPendingRequestCounts,
        collaborationSidebarCounts,
        refreshCollaborationSidebarCounts: fetchCollaborationSidebarCounts,
        syncGuestShortlistToServer,
        maintenanceEnabled,
        refetchMaintenance: fetchMaintenance,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProvider = UserContextProvider;
export const useUser = useUserContext;
